/**
 * Guided tours as data (todos/ui.md, sketch docs/design/sketches/guided-tours.html).
 *
 * A tour is a list of steps. Each step lights one real control, found by its
 * `data-tour="…"` anchor, never by a class name, so a style change cannot break
 * a tour. A new tour is one file in `tours/` that calls `registerTour`; the
 * engine, the welcome pop-up and the tours list do not change.
 */

import { openSection } from '../../state/open-section.ts';

export type TourAdvance =
  /** A Next button on the card. */
  | 'next'
  /** A click on the lit control. */
  | 'click'
  /** Something became true, for example a ship is picked or a chart is saved. */
  | { when: () => boolean };

export interface TourStep {
  /** The `data-tour` anchor to light, or a function for a control made at run time. */
  target: string | (() => Element | null);
  title: string;
  text: string;
  advance: TourAdvance;
  /** Leave the step out when this is false, for example Save without a database. */
  needs?: () => boolean;
  /** Pass over the step when it is reached and this is true: it depends on an earlier, skipped step. */
  skipIf?: () => boolean;
  /** Wait (page dim, card says so) until this is true: a chart that is drawing. */
  ready?: () => boolean;
  /** When the target goes away (a dialog was cancelled), go back one step. */
  backIfGone?: boolean;
  /** While not ready: a sentence when the step cannot go on (the plot failed), else null. */
  failed?: () => string | null;
  /** Runs when the step starts, before `when` is read: note what "changed" is measured from. */
  enter?: () => void;
  /** `data-tour` anchors that cannot be clicked on this step (shown faint): the other way to do it. */
  blocked?: string[];
  /** A `when` step that may be hard to do (a drag): the card also offers Skip. */
  skippable?: boolean;
}

export interface Tour {
  id: string;
  /** Goes up when the steps change, so old runs can be told from new ones. */
  version: number;
  title: string;
  description: string;
  minutes: number;
  /** Offer the tour only to people who can finish it (roles and Features). */
  available: () => boolean;
  /** Tours to finish first. The tour is shown, locked, until they are finished. */
  requires?: string[];
  /** The tour to offer on the last step's card. */
  next?: string;
  /** Take the person to the right page before the first step. */
  prepare?: () => void;
  steps: TourStep[];
}

const tours: Tour[] = [];

export function registerTour(tour: Tour): void {
  if (!tours.some((t) => t.id === tour.id)) tours.push(tour);
}

export function tourById(id: string): Tour | undefined {
  return tours.find((t) => t.id === id);
}

/** The tours this person can finish, in the order they were registered. */
export function availableTours(): Tour[] {
  return tours.filter((t) => t.available());
}

/** The steps that apply now. Fixed when the tour starts. */
export function activeSteps(tour: Tour): TourStep[] {
  return tour.steps.filter((s) => !s.needs || s.needs());
}

/**
 * Open an Explore tab from anywhere: ask for Explore, then click the tab's
 * button once the section has drawn it (it is not on the page from Home or
 * Dashboards until then).
 */
export function openExploreTab(tab: string): void {
  const root = document.querySelector('app-root');
  if (root) openSection(root, 'explore');
  let frames = 0;
  const click = (): void => {
    const btn = document.querySelector<HTMLElement>(`[data-tab-btn="${CSS.escape(tab)}"]`);
    if (btn) btn.click();
    else if (++frames < 120) requestAnimationFrame(click);
  };
  click();
}

export function resolveTarget(step: TourStep): HTMLElement | null {
  const el =
    typeof step.target === 'function'
      ? step.target()
      : document.querySelector(`[data-tour="${CSS.escape(step.target)}"]`);
  return el instanceof HTMLElement ? el : null;
}
